import type Luminary from '@lumen5/luminary';
import type { AssetGroup, TextAsset } from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';

import { getMinimumResizeHeight } from 'js/src/luminary/luminary-selectors';

type HeightParameterName = 'height' | 'max_height';

export function applyMaxHeight(
    luminary: Luminary,
    box: AssetGroup | TextAsset,
    value: number,
    parameterName: HeightParameterName,
): { change: string } {
    const subject = parameterName === 'max_height' ? 'group' : 'box';
    // getNormalizedAssetBounds divides the editor's overlay by this height, so never let it be 0.
    const applied = Math.max(value, getMinimumResizeHeight(box));

    luminary.batchRun(() => {
        box.maxHeight = applied;
        // Without the capability, nothing but this flag makes a height the agent sets take effect.
        if (!luminary.enableEnhancedAssetTransform) {
            box.hasUserChangedMaxHeight = true;
        }
    });

    if (applied !== value) {
        return {
            change: `${parameterName} to ${applied}px, the shortest the ${subject} can be (${value}px was asked for)`,
        };
    }
    return { change: `${parameterName} to ${value}px` };
}

/**
 * Stops one box growing to fit what the agent writes into it, so the copy is fitted and clipped
 * against the box the design drew and `text_clipping` reports copy that is too long. Scoped to the
 * box being written: a box the agent never addressed must keep following its own text, or an
 * untouched headline collapses and reports as clipped. Not serialized, so none of it is saved.
 */
export function suspendTextAutoResize(luminary: Luminary, box: AssetGroup | TextAsset): void {
    if (!luminary.enableEnhancedAssetTransform) {
        return;
    }
    box.heightGovernor.shouldAutoResizeToFitText = false;
}

/** Hands every box back to its text once the agent has stopped writing. */
export function restoreTextAutoResize(luminary: Luminary): void {
    if (!luminary.enableEnhancedAssetTransform) {
        return;
    }
    luminary.batchRun(() => {
        luminary.scenes.forEach((scene) => {
            scene.getAssetsByType(VideoData.TEXT_ASSET_TYPE).forEach((asset) => {
                asset.heightGovernor.shouldAutoResizeToFitText = true;
            });
        });
    });
}
